import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Collapse, Form, Input, InputNumber, Row, Col, Spin, Table, message } from 'antd';
import api from '../../services/api';

type Value = string | number | boolean | null;
type Values = Record<string, Value>;

const checkboxFields = [
  ['Amended_return', 'Amended return'], ['Federal_audit', 'Federal audit pending'],
  ['DeathCert', 'Death certificate attached'], ['DecedantNonResident', 'Decedent was a nonresident'],
  ['PoAX', 'Power of attorney attached'], ['ExcecutorX', 'More than one executor'],
  ['ScheduleF', 'Federal Schedule F election'], ['Installment', 'Installment payment election'],
  ['FedEstate', 'Federal estate-tax return required'], ['Terminable', 'Terminable-interest election'],
  ['Attorney', 'Prepared by attorney'], ['PA', 'Prepared by public accountant'],
  ['Agent', 'Prepared by enrolled agent'], ['NYSEdu', 'New York tax preparer education requirement applies'],
] as const;

const textFields = [
  ['Name_EINTrusts', 'Name and EIN of trusts created or funded by the will'],
  ['PoA_Form', 'Power of attorney form'], ['PoA_Date', 'Power of attorney date'],
  ['Executor_Letters', 'Executor letters information'], ['SurrogateCourt', "Surrogate's Court"],
  ['ICO_firm', 'Attorney or representative firm'], ['Rep_first_name', 'Representative first name'],
  ['Rep_MI', 'Representative middle initial'], ['Rep_SSN', 'Representative PTIN or SSN'],
  ['Rep_address', 'Representative address'], ['Rep_city', 'Representative city'],
  ['Rep_State', 'Representative state'], ['Rep_ZIP', 'Representative ZIP'],
  ['Rep_phone', 'Representative telephone'], ['Rep_email', 'Representative email'],
  ['Counties', 'New York counties containing real property'], ['SSNSurviving', 'Surviving spouse SSN'],
  ['F_Description_litigation', 'Pending estate-tax litigation description'],
  ['AttorneyEmail', 'Attorney email'], ['PrintPreparer', 'Preparer name'],
  ['PrepSSN', 'Preparer SSN or PTIN'], ['PrepNYTPRIN', 'New York preparer registration number'],
  ['AddPrep', 'Preparer address'], ['PrepCity', 'Preparer city'], ['PrepState', 'Preparer state'],
  ['PrepZIP', 'Preparer ZIP'], ['PrepEmail', 'Preparer email'],
  ['Datesign', 'Executor signature date'], ['DateEX', 'Executor date'],
  ['DateEXCO', 'Co-executor date'], ['DatePrep', 'Preparer signature date'],
] as const;

const scheduleLines = [
  [8, 'Federal gross estate, resident'], [11, 'Pre-1930 power of appointment'],
  [13, 'New York QTIP inclusion'], [15, 'Allowable federal deductions'],
  [19, 'Federal gross estate, nonresident'], [21, 'Intangible property'],
  [24, 'Pre-1930 power of appointment, nonresident'], [26, 'New York QTIP inclusion, nonresident'],
  [28, 'Allowable federal deductions, nonresident'], [32, 'Works of art on loan in New York'],
] as const;

const mainLines = [
  [5, 'Net prior tax payments to New York State'],
] as const;

const deductionRows = [
  [5, 'Net losses during administration'], [6, 'Administration expenses not subject to claims'],
  [7, 'Bequests to surviving spouse'], [8, 'Charitable and similar bequests'],
] as const;

function MoneyPair({ line, prefix = 'Line8' }: { line: number; prefix?: 'Line8' | 'Line' }) {
  const index = prefix === 'Line8' ? line - 8 : line;
  const dollars = prefix === 'Line8' ? `Line8.${index}` : `Line${index}`;
  const cents = prefix === 'Line8' ? `CentsLine8.${index}` : `CentsLine${index}`;
  return <Input.Group compact>
    <Form.Item name={dollars} noStyle><InputNumber min={0} precision={0} placeholder="Dollars" style={{width:'75%'}} /></Form.Item>
    <Form.Item name={cents} noStyle><InputNumber min={0} max={99} precision={0} placeholder="Cents" style={{width:'25%'}} /></Form.Item>
  </Input.Group>;
}

export default function Et706Editor({ estateId }: { estateId: string }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stored, setStored] = useState<Values>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!estateId) return;
    setLoading(true);
    api.get(`/estates/${estateId}/et-706`).then(({data}) => {
      const values = data.values || {};
      setStored(values); form.setFieldsValue(values); setError('');
    }).catch(() => setError('Unable to load the ET-706 return data')).finally(() => setLoading(false));
  }, [estateId, form]);

  const save = async (changed: Values) => {
    setSaving(true);
    try {
      const values = { ...stored, ...changed };
      await api.put(`/estates/${estateId}/et-706`, { values });
      setStored(values); message.success('ET-706 return data saved');
    } catch (e: any) { message.error(e.response?.data?.message || 'Could not save ET-706 return data'); }
    finally { setSaving(false); }
  };

  if (loading) return <Card title="NYS estate tax return (ET-706)" style={{marginTop:24}}><Spin /></Card>;
  return <Card title="NYS estate tax return (ET-706)" style={{marginTop:24}}>
    {error && <Alert type="error" message={error} />}
    <Alert type="info" showIcon message="Assets, jurisdiction, date-of-death values, deductible claims, and arithmetic are filled from Wzee. Enter only the tax-return facts that cannot be derived from the estate ledger." style={{marginBottom:16}} />
    <Form form={form} layout="vertical" onFinish={save}>
      <Collapse items={[
        { key:'filing', label:'Filing elections and representative', children:<>
          <Row gutter={16}>{checkboxFields.map(([key,label]) => <Col xs={24} md={12} key={key}><Form.Item name={key} valuePropName="checked"><Checkbox>{label}</Checkbox></Form.Item></Col>)}</Row>
          <Row gutter={16}>{textFields.map(([key,label]) => <Col xs={24} md={12} key={key}><Form.Item name={key} label={label}><Input /></Form.Item></Col>)}</Row>
        </>},
        { key:'schedule-a', label:'Schedule A and tax computation inputs', children:<>
          <Alert type="warning" showIcon message="Use the amounts reported on the federal estate-tax return. Wzee calculates the dependent New York lines." style={{marginBottom:16}} />
          <Row gutter={16}>{scheduleLines.map(([line,label]) => <Col xs={24} md={12} key={line}><Form.Item label={`Line ${line}: ${label}`}><MoneyPair line={line} /></Form.Item></Col>)}</Row>
          <Row gutter={16}>{mainLines.map(([line,label]) => <Col xs={24} md={12} key={line}><Form.Item label={`Line ${line}: ${label}`}><MoneyPair line={line} prefix="Line" /></Form.Item></Col>)}</Row>
        </>},
        { key:'gifts', label:'Schedule D taxable gifts', children:<Table pagination={false} size="small" rowKey="index" dataSource={Array.from({length:13},(_,index)=>({index}))} columns={[
          {title:'#',dataIndex:'index',render:(i:number)=>i+1,width:45},
          {title:'Date',render:(_:unknown,r:{index:number})=><Form.Item name={`D_Date_made.${r.index}`} noStyle><Input type="date" /></Form.Item>},
          {title:'Description and location',render:(_:unknown,r:{index:number})=><Form.Item name={`D_Description.${r.index}`} noStyle><Input /></Form.Item>},
          {title:'Taxable amount',render:(_:unknown,r:{index:number})=><Form.Item name={`D_Taxable_amount.${r.index}`} noStyle><InputNumber min={0} precision={2} style={{width:'100%'}} /></Form.Item>},
        ]} />},
        { key:'deductions', label:'Schedule E tax-only deductions', children:<>
          <p>Funeral expenses, debts, mortgages, and liens come from the liability records. Enter Schedule L, M, and O amounts below.</p>
          {(['E1_chart','E2_chart'] as const).map((prefix) => <Card key={prefix} size="small" title={prefix === 'E1_chart' ? 'Resident deductions' : 'Nonresident deductions'} style={{marginBottom:12}}>
            <Table pagination={false} size="small" rowKey="row" dataSource={deductionRows.map(([row,label])=>({row,label}))} columns={[
              {title:'Deduction',dataIndex:'label'},
              ...['Federal total','NY / inside NY','Outside NY / intangible','Unallocated'].map((title,column)=>({title,render:(_:unknown,r:{row:number})=><Form.Item name={`${prefix}.${r.row}.${column}`} noStyle><InputNumber min={0} precision={2} style={{width:'100%'}} /></Form.Item>})),
            ]} />
          </Card>)}
        </>},
      ]} />
      <Button type="primary" htmlType="submit" loading={saving} style={{marginTop:16}}>Save ET-706 return data</Button>
    </Form>
  </Card>;
}
